import React, { useContext, useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import { Dispatch, bindActionCreators } from "redux";

import { Dialog, CancelLink, Button } from "@plex/react-components";

import { commonMessages } from "infrastructure/localization";
import { INotification } from "infrastructure/notification";
import { Banner } from "ui/app";
import { DialogContainer, DialogNavigationContext } from "ui/app/navigation";
import { Upload } from "ui/components";
import { useLocalizedValues } from "ui/hooks";
import commonStyles from "ui/screens/translation-managers/shared/styles/Translations.module.scss";
import { displayMessages } from "ui/screens/translation-managers/translation-managers.localization";

import { plexTranslationUploadActions } from "./PlexTranslationUpload.actions";
import { IPlexTranslationUploadState, IPlexTranslationUploadError } from "./PlexTranslationUpload.state";

export interface IPlexTranslationUploadProps {
  progress: number;
  error?: IPlexTranslationUploadError;
  uploadProcessing: boolean;
  uploadCompleted: boolean;
  notifications: INotification[];
  onSubmit: (file: File[]) => void;
  resetState: () => void;
  onUploadCompleted: (error?: IPlexTranslationUploadError) => void;
}

const PlexTranslationUpload: React.FC<IPlexTranslationUploadProps> = ({
  error,
  onSubmit,
  uploadProcessing,
  uploadCompleted,
  notifications,
  resetState,
  onUploadCompleted,
  progress
}) => {
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);

  const dialogNavigationContext = useContext(DialogNavigationContext);

  const localizedValues = useLocalizedValues(() => ({
    dialogTitle: displayMessages.title("selectPlexTranslationsUploadFile"),
    submit: commonMessages.action("submit")
  }));

  const updateAttachedFilesList = useCallback(
    (files: File[]) => {
      setAttachedFiles(files);
      resetState();
    },
    [resetState]
  );

  const onHidden = useCallback(() => {
    if (!error) {
      resetState();
    }
  }, [error, resetState]);

  const onSubmitExecute = useCallback(() => {
    onSubmit(attachedFiles);
  }, [onSubmit, attachedFiles]);

  useEffect(() => {
    if (uploadCompleted) {
      dialogNavigationContext.hideDialog(undefined, onHidden);
      onUploadCompleted(error);
    }
  }, [uploadCompleted, onUploadCompleted, error, dialogNavigationContext, onHidden]);

  return (
    <DialogContainer
      onHidden={onHidden}
      render={dialogProps => {
        return (
          <Dialog {...dialogProps} title={localizedValues.dialogTitle}>
            <Dialog.Body>
              <Banner messages={notifications} messageClassName={commonStyles.dialogBanner} />
              <Upload onFilesAttached={updateAttachedFilesList} uploadingProcessing={uploadProcessing} progress={progress} accept={[".xml"]} id="plexTranslationUploader" />
            </Dialog.Body>
            <Dialog.Footer>
              <CancelLink onClick={dialogProps.onHide} />
              <Button onClick={onSubmitExecute}>{localizedValues.submit}</Button>
            </Dialog.Footer>
          </Dialog>
        );
      }}
    />
  );
};

export default connect(
  (state: { plexTranslationsUpload: IPlexTranslationUploadState }) => {
    return {
      error: state.plexTranslationsUpload.error,
      uploadProcessing: state.plexTranslationsUpload.uploadProcessing,
      uploadCompleted: state.plexTranslationsUpload.uploadCompleted,
      notifications: state.plexTranslationsUpload.notifications,
      progress: state.plexTranslationsUpload.progress
    };
  },
  (dispatch: Dispatch) => {
    return bindActionCreators(
      {
        onSubmit: plexTranslationUploadActions.uploadPlexTranslation,
        resetState: plexTranslationUploadActions.resetUploadingPlexTranslation
      },
      dispatch
    );
  }
)(PlexTranslationUpload);
